import { EventAPI } from '@praxis/analytics'
import moment from 'moment-timezone'
import { trackSession, sessionId, generateSessionId } from './initializeFirefly'
import config from '../config/apiConfig'
import { EventCategories } from './eventTypes'

const Analytics = {
  eventType: {
    CLICK: 'CLICK',
    VIEW: 'PAGE_VIEW',
    LOGIN: 'LOGIN',
    UNINITIATED_DOWNLOAD: 'UNINITIATED_DOWNLOAD',
    ASSET_CARD_DOWNLOAD: 'ASSET_CARD_DOWNLOAD',
    DOWNLOAD: 'DOWNLOAD',
    PROJECT_CREATE: 'PROJECT_CREATE',
    PROJECT_LIST_SORT: 'PROJECT_LIST_SORT',
    PROJECT_LIST_FILTER: 'PROJECT_LIST_FILTER',
    PROJECT_LIST_PAGE: 'PROJECT_LIST_PAGE',
    SEARCH_PAGE_CHANGE: 'search_page_change',
    SEARCH_PAGE_SORT_CHANGE: 'search_page_sort_change',
    SEARCH_PAGE_RESULTS_CHANGE: 'search_page_results_change',
    BOARD_CREATE: 'BOARD_CREATE',
    BOARD_UPDATE: 'BOARD_UPDATE',
    BOARD_DELETE: 'BOARD_DELETE',
    BOARD_ADD_ASSET: 'BOARD_ADD_ASSET',
    BOARD_REMOVE_ASSET: 'BOARD_REMOVE_ASSET',
    UPDATE_ASSET_METADATA: 'UPDATE_ASSET_METADATA',
    BULK_UPDATE_ASSET_METADATA: 'BULK_UPDATE_ASSET_METADATA',
    SEARCH_HIGHLIGHTS: 'SEARCH_HIGHLIGHTS',
    LIBRARIAN_TEMPLATE_CHANGE: 'LIBRARIAN_TEMPLATE_CHANGE',
    PUBLISH_ASSET_COPY: 'PUBLISH_ASSET_COPY',
  },
  SCHEMA_VERSION: '1.2.2',
}

let searchId = ''

const analyticsConfig = {
  appName: 'ASSETHUB_UI',
  apiKey: config?.analytics?.key || 'test-key',
  url: config?.analytics?.url,
}
const analyticsInstance = new EventAPI(analyticsConfig)

export const searchFilterKey = (searchTerm) => {
  if (searchTerm === '*' || !searchTerm) return 'EmptySearch'
  if (/^[\d -.; _]+$/.test(searchTerm)) return 'TcinDpciSearch'

  const extensions = [
    'pdf',
    'tif',
    'tiff',
    'ai',
    'jpg',
    'jpeg',
    'psd',
    'eps',
    'png',
    'dms',
    'indd',
    'ind',
    'psb',
    'mov',
    'mp4',
  ]
  const searchExtensionSplit = searchTerm.toLowerCase().split('.')
  for (let extension in searchExtensionSplit) {
    if (extensions.includes(searchExtensionSplit[extension])) {
      return 'FileNameSearch'
    }
  }
  return 'KeywordSearch'
}

const getSessionId = (loginId = '') => {
  if (!sessionId && loginId) generateSessionId(loginId)
  return sessionId
}

const sendFireflyEvent = (eventType, eventData = {}, loginId = '') => {
  const eventTimestamp = moment.utc().format('YYYY-MM-DDTHH:mm:ssZ')
  trackSession(loginId, eventTimestamp)

  const { category, customInteraction, user, customMetrics, ...restEventData } =
    eventData

  return analyticsInstance.sendEvent({
    event: {
      type: eventType,
      timestamp: eventTimestamp,
      category,
      customSessionId: getSessionId(loginId),
      ...restEventData,
    },
    ...(customInteraction && { customInteraction }),
    ...(user && { user }),
    ...(customMetrics && { customMetrics }),
  })
}

const firefly = {
  initalizeSearchId() {
    if (searchId === '') searchId = new Date().toISOString()
  },
  clearSearchId() {
    searchId = ''
  },
  trackLogin(loginId) {
    generateSessionId(loginId)
    return sendFireflyEvent(
      Analytics.eventType.LOGIN,
      { category: EventCategories.CLICK },
      loginId,
    )
  },
  trackView(route, title, loginId, prevPath) {
    return sendFireflyEvent(
      Analytics.eventType.VIEW,
      {
        route,
        title,
        previousPath: prevPath,
        category: EventCategories.CLICK,
      },
      loginId,
    )
  },
  trackUninitiatedDownloads(uninitiatedDownloads = 0, loginId) {
    return sendFireflyEvent(
      Analytics.eventType.UNINITIATED_DOWNLOAD,
      {
        category: EventCategories.DOWNLOAD,
        customInteraction: {
          key: 'uninitiatedDownloadCount',
          value: `${uninitiatedDownloads}`,
        },
      },
      loginId,
    )
  },
  trackClick(action, actionType, searchTerm, filterData, loginId, templateId) {
    return sendFireflyEvent(
      action,
      {
        category: EventCategories.CLICK,
        customInteraction: {
          key: searchFilterKey(searchTerm),
          value: `${searchTerm}`,
        },
        user: {
          customDimension1: `actionType# ${actionType}`,
          customDimension2: `searchTerm# ${searchTerm}`,
          customDimension3: `filters# ${filterData}`,
          segment: `template# ${templateId}`,
          taskId: `taskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackSearchSort(
    searchTerm,
    filterData,
    loginId,
    currentPage,
    sortBy,
    templateId,
  ) {
    return sendFireflyEvent(
      Analytics.eventType.SEARCH_PAGE_SORT_CHANGE,
      {
        category: EventCategories.SEARCH,
        customInteraction: {
          key: 'SearchPageSortChange',
          value: `${sortBy}`,
          location: `${currentPage + 1}`,
        },
        user: {
          customDimension1: `actionType# Page Sort Change`,
          customDimension2: `searchTerm# ${searchTerm}`,
          customDimension3: `filters# ${filterData}`,
          segment: `template# ${templateId}`,
          taskId: `taskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackSearchResultsPerPageChange(
    searchTerm,
    filterData,
    loginId,
    currentPage,
    currentPageSize,
    templateId,
  ) {
    return sendFireflyEvent(
      Analytics.eventType.SEARCH_PAGE_RESULTS_CHANGE,
      {
        category: EventCategories.SEARCH,
        customInteraction: {
          key: 'SearchPageSizeChange',
          value: `${currentPageSize}`,
          location: `${currentPage + 1}`,
        },
        user: {
          customDimension1: `actionType# Page Size Change`,
          customDimension2: `searchTerm# ${searchTerm}`,
          customDimension3: `filters# ${filterData}`,
          segment: `template# ${templateId}`,
          taskId: `taskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackSearchPageChange(
    searchTerm,
    filterData,
    loginId,
    currentPage,
    currentPageSize,
    templateId,
  ) {
    return sendFireflyEvent(
      Analytics.eventType.SEARCH_PAGE_CHANGE,
      {
        category: EventCategories.SEARCH,
        customInteraction: {
          key: 'SearchPageChange',
          value: `${currentPage + 1}`,
          location: `${currentPageSize}`,
        },
        user: {
          customDimension1: `actionType# Page Change`,
          customDimension2: `searchTerm# ${searchTerm}`,
          customDimension3: `filters# ${filterData}`,
          segment: `template# ${templateId}`,
          taskId: `taskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackLibrarianTemplateChange(templateId, loginId) {
    return sendFireflyEvent(
      Analytics.eventType.TEMPLATE_LIBRARIAN_CHANGE,
      {
        category: EventCategories.TEMPLATE,
        customInteraction: {
          key: 'TemplateChange',
          value: `${templateId}`,
        },
        user: {
          customDimension1: `actionType# Template Change`,
          customDimension2: '',
          customDimension3: '',
          segment: `template# ${templateId}`,
          taskId: `taskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackUpload(action, actionType, fileName, uploadFileData, loginId) {
    return sendFireflyEvent(
      action,
      {
        category: EventCategories.UPLOAD,
        customInteraction: {
          key: 'Upload',
          value: `${fileName}`,
        },
        user: {
          customDimension1: `actionType# ${actionType}`,
          customDimension2: `fileName# ${fileName}`,
          customDimension3: `uploadData# ${uploadFileData}`,
          segment: '',
          id: '',
        },
      },
      loginId,
    )
  },
  trackApiError(error = {}, loginId) {
    const eventTimestamp = moment.utc().format('YYYY-MM-DDTHH:mm:ssZ')
    return analyticsInstance.sendEvent({
      event: {
        type: 'ERROR',
        timestamp: eventTimestamp,
        value: error?.message,
        customSessionId: getSessionId(loginId),
      },
      interface_message: {
        errorMessage: error?.message || 'Network Error',
        errorUrl: `errorUrl# ${error?.config?.url}`,
        error_code: error?.code || '404',
      },
    })
  },
  trackProjectCreate(project = {}, loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.PROJECT_CREATE,
      {
        category: EventCategories.PROJECT,
        customInteraction: {
          key: Analytics.eventType.PROJECT_CREATE,
          value: JSON.stringify(project),
        },
      },
      loginId,
    )
  },
  trackProjectListSort(projectListSort = '', loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.PROJECT_LIST_SORT,
      {
        category: EventCategories.PROJECT,
        customInteraction: {
          key: Analytics.eventType.PROJECT_LIST_SORT,
          value: projectListSort,
        },
      },
      loginId,
    )
  },
  trackProjectListFilter(filter = '', loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.PROJECT_LIST_FILTER,
      {
        category: EventCategories.PROJECT,
        customInteraction: {
          key: Analytics.eventType.PROJECT_LIST_FILTER,
          value: filter,
        },
      },
      loginId,
    )
  },
  trackProjectListPage(page = 1, pageSize = 0, loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.PROJECT_LIST_PAGE,
      {
        category: EventCategories.PROJECT,
        customInteraction: {
          key: Analytics.eventType.PROJECT_LIST_PAGE,
          value: `page: ${page}, size: ${pageSize}`,
        },
      },
      loginId,
    )
  },
  trackBoardCreated(boardId = '', loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.BOARD_CREATE,
      {
        category: EventCategories.BOARD,
        customInteraction: {
          key: Analytics.eventType.BOARD_CREATE,
          value: boardId,
        },
      },
      loginId,
    )
  },
  trackBoardUpdated(boardId = '', loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.BOARD_UPDATE,
      {
        category: EventCategories.BOARD,
        customInteraction: {
          key: Analytics.eventType.BOARD_UPDATE,
          value: boardId,
        },
      },
      loginId,
    )
  },
  trackBoardDeleted(boardId = '', loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.BOARD_DELETE,
      {
        category: EventCategories.BOARD,
        customInteraction: {
          key: Analytics.eventType.BOARD_DELETE,
          value: boardId,
        },
      },
      loginId,
    )
  },
  trackBoardAddAsset(data = '', loginId = '', location = '') {
    const boards = []
    for (const [key, value] of Object.entries(data)) {
      const assets = []
      value.forEach((asset) => assets.push(asset.asset_id))
      boards.push({ board_id: key, assets: assets })
    }
    return sendFireflyEvent(
      Analytics.eventType.BOARD_ADD_ASSET,
      {
        category: EventCategories.BOARD,
        customInteraction: {
          key: Analytics.eventType.BOARD_ADD_ASSET,
          value: JSON.stringify(boards),
        },
      },
      loginId,
    )
  },
  trackBoardRemoveAsset(data = {}, loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.BOARD_REMOVE_ASSET,
      {
        category: EventCategories.BOARD,
        customInteraction: {
          key: Analytics.eventType.BOARD_REMOVE_ASSET,
          value: JSON.stringify(data),
        },
      },
      loginId,
    )
  },
  trackMetadataUpdate(metadata = {}, loginId = '', location = '') {
    return sendFireflyEvent(
      Analytics.eventType.UPDATE_ASSET_METADATA,
      {
        category: EventCategories.METADATA,
        customInteraction: {
          key: Analytics.eventType.UPDATE_ASSET_METADATA,
          value: metadata.assetId,
        },
        user: {
          taskId: location === 'search' ? `taskId: ${searchId}` : '',
        },
      },
      loginId,
    )
  },
  trackBulkMetadataUpdate(data = {}, loginId = '', location = '') {
    return sendFireflyEvent(
      Analytics.eventType.BULK_UPDATE_ASSET_METADATA,
      {
        category: EventCategories.METADATA,
        customInteraction: {
          key: Analytics.eventType.BULK_UPDATE_ASSET_METADATA,
          value: JSON.stringify(data),
        },
        user: {
          taskId: location === 'search' ? searchId : '',
        },
      },
      loginId,
    )
  },
  trackSearchHighlights(highlights = {}, loginId = '') {
    return sendFireflyEvent(
      Analytics.eventType.SEARCH_HIGHLIGHTS,
      {
        category: EventCategories.SEARCH,
        customInteraction: {
          key: Analytics.eventType.SEARCH_HIGHLIGHTS,
          value: JSON.stringify(highlights),
        },
        user: {
          customDimension1: '',
          customDimension2: '',
          customDimension3: '',
          segment: '',
          taskId: `taskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackPublishSearchClick(
    action = '',
    actionType = '',
    searchTerm = '',
    filterData = '',
    loginId = '',
  ) {
    firefly.initalizeSearchId()
    return sendFireflyEvent(
      action,
      {
        category: EventCategories.PUBLISH,
        customInteraction: {
          key: searchFilterKey(searchTerm),
          value: `${searchTerm}`,
        },
        user: {
          customDimension1: `publishActionType# ${actionType}`,
          customDimension2: `publishSearchTerm# ${searchTerm}`,
          customDimension3: `publishFilters# ${filterData}`,
          segment: '',
          taskId: `publishTaskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackPublishAssetCardDownloads(eventType = '', downloadAsset, loginId = '') {
    firefly.initalizeSearchId()
    return sendFireflyEvent(
      eventType,
      {
        category: EventCategories.PUBLISH,
        customInteraction: {
          key: 'publishDownloadCount',
          value: '1',
        },
        user: {
          customDimension1: '',
          customDimension2: `downloadFiles# ${JSON.stringify(downloadAsset)}`,
          customDimension3: '',
          segment: '',
          taskId: `taskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackPublishAssetCopy(data = {}, loginId = '') {
    firefly.initalizeSearchId()
    return sendFireflyEvent(
      Analytics.eventType.PUBLISH_ASSET_COPY,
      {
        category: EventCategories.PUBLISH,
        customInteraction: {
          key: Analytics.eventType.PUBLISH_ASSET_COPY,
          value: JSON.stringify(data),
        },
        user: {
          customDimension1: '',
          customDimension2: '',
          customDimension3: '',
          segment: '',
          taskId: `taskId: ${searchId}`,
        },
      },
      loginId,
    )
  },
  trackHomePageClicks(loginId = '', clickType = '') {
    return sendFireflyEvent(
      Analytics.eventType.CLICK,
      {
        category: EventCategories.CLICK,
        customInteraction: {
          key: Analytics.eventType.CLICK,
          value: clickType,
        },
      },
      loginId,
    )
  },
}

export default firefly
export { Analytics }
